import {
  CSTBinaryOperationNode,
  CSTConditionNode,
  CSTEquationNode,
  CSTErrorNode,
  CSTKeyValueNode,
  CSTListNode,
  CSTNode,
  CSTParenthesesNode,
  CSTValueNode,
} from './CST';
import { isNode, isToken } from './Filters.typeguards';
import { SyntaxKind } from './SyntaxKind';
import { Token } from './Token';

const EquationOperators = [
  SyntaxKind.Equals,
  SyntaxKind.NotEquals,
  SyntaxKind.LessThan,
  SyntaxKind.LessOrEqualThan,
  SyntaxKind.MoreThan,
  SyntaxKind.MoreOrEqualThan,
  SyntaxKind.Contains,
  SyntaxKind.NotContains,
  SyntaxKind.Search,
  SyntaxKind.IsEmpty,
  SyntaxKind.IsNotEmpty,
];

const SingleValueOperators = [
  SyntaxKind.MoreThan,
  SyntaxKind.MoreOrEqualThan,
  SyntaxKind.LessThan,
  SyntaxKind.LessOrEqualThan,
  SyntaxKind.Search,
  SyntaxKind.Contains,
  SyntaxKind.NotContains,
];

const BinaryOperators = [SyntaxKind.And, SyntaxKind.Or];

export enum CodeCompletionContext {
  Variable,
  Value,
  None,
}

export function parse(tokens: Token[]): CSTConditionNode {
  let i = 0;
  const tokensLength = tokens.length;
  const root: CSTConditionNode = {
    type: 'condition',
    begin: 0,
    end: tokens.length > 0 ? tokens[tokensLength - 1].end : 0,
    children: [],
  };

  function check() {
    return tokens[i];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function peek(k = 1) {
    return tokens[i + k];
  }

  function eat(syntaxKind: SyntaxKind, errorMessage = 'Undefined error'): CSTErrorNode | Token {
    const eofErrorNode: CSTErrorNode = {
      type: 'error',
      begin: tokens[tokensLength - 1].end,
      end: tokens[tokensLength - 1].end,
      message: 'Unexpected EOF',
      children: [],
    };

    if (i >= tokensLength) {
      return eofErrorNode;
    }

    if (tokens[i].syntaxKind === SyntaxKind.EOF) {
      // eofErrorNode.children.push(tokens[i]);
      ++i;

      return eofErrorNode;
    }

    const token = tokens[i];

    if (token.syntaxKind === syntaxKind) {
      ++i;

      return token;
    }

    ++i;

    return {
      type: 'error',
      begin: token.begin,
      end: token.end,
      message: errorMessage,
      children: [token],
    };
  }

  function eatOneOf(syntaxKinds: SyntaxKind[], errorMessage = 'Undefined error'): CSTErrorNode | Token {
    const eofErrorNode: CSTErrorNode = {
      type: 'error',
      begin: tokens[tokensLength - 1].end,
      end: tokens[tokensLength - 1].end,
      message: 'Unexpected EOF',
      children: [],
    };

    if (i >= tokensLength) {
      return eofErrorNode;
    }

    if (tokens[i].syntaxKind === SyntaxKind.EOF) {
      // eofErrorNode.children.push(tokens[i]);
      ++i;

      return eofErrorNode;
    }

    const token = tokens[i];

    if (syntaxKinds.includes(token.syntaxKind)) {
      ++i;

      return token;
    }

    ++i;

    return {
      type: 'error',
      begin: token.begin,
      end: token.end,
      message: errorMessage,
      children: [token],
    };
  }

  function extendWhiteSpace(node: CSTNode) {
    const token = check();

    if (token && isNode(node) && node.end < token.begin) {
      node.end = token.begin;
    }
  }

  function parseParentheses(): CSTParenthesesNode {
    const leftParen = eat(SyntaxKind.LeftParen);
    const children: (CSTNode | Token)[] = [];

    while (i < tokensLength) {
      const node = parseCondition();

      children.push(node);

      if (node.type !== 'error') {
        break;
      }
    }

    const rightParen = eat(SyntaxKind.RightParen);

    const parenthesesNode: CSTParenthesesNode = {
      type: 'parentheses',
      begin: leftParen.begin,
      end: rightParen.end,
      leftParen,
      rightParen,
      children,
    };

    extendWhiteSpace(parenthesesNode);

    return parenthesesNode;
  }

  function parseList(firstItem: CSTNode | Token): CSTListNode {
    // we already parsed first item, we start from comma here

    const listNode: CSTListNode = {
      type: 'list',
      begin: firstItem.begin,
      end: firstItem.end,
      children: [firstItem],
    };

    while (i < tokensLength) {
      const token = check();

      if (token.syntaxKind === SyntaxKind.RightParen || token.syntaxKind !== SyntaxKind.Comma) {
        break;
      }

      const comma = eat(SyntaxKind.Comma);
      const value = parseValue();

      listNode.children.push(comma);
      listNode.children.push(value);

      listNode.end = value.end;
    }

    return listNode;
  }

  function parseKeyValue(key: Token): CSTKeyValueNode {
    const colon = eat(SyntaxKind.Colon);
    const value = eatOneOf(
      [SyntaxKind.Integer, SyntaxKind.Float, SyntaxKind.String, SyntaxKind.Identifier],
      'Expected value: integer, float or string',
    );

    const keyValueNode: CSTKeyValueNode = {
      type: 'keyValue',
      begin: key.begin,
      end: value.end,
      key,
      colon,
      value,
    };

    return keyValueNode;
  }

  function parseValue(): CSTValueNode {
    const result = eatOneOf(
      [SyntaxKind.Integer, SyntaxKind.Float, SyntaxKind.String, SyntaxKind.Identifier],
      'Expected value: integer, float or string',
    );
    let kvNode: CSTKeyValueNode | undefined = undefined;

    const token = check();

    if (isToken(result) && token && token.syntaxKind === SyntaxKind.Colon) {
      kvNode = parseKeyValue(result);
    }

    const afterKvNodeToken = check();

    if (kvNode && afterKvNodeToken && afterKvNodeToken.syntaxKind === SyntaxKind.Comma) {
      const list = parseList(kvNode);
      const valueNode: CSTValueNode = {
        type: 'value',
        begin: list.begin,
        end: list.end,
        value: list,
      };

      return valueNode;
    }

    if (isToken(result) && token && token.syntaxKind === SyntaxKind.Comma) {
      const list = parseList(result);

      const valueNode: CSTValueNode = {
        type: 'value',
        begin: list.begin,
        end: list.end,
        value: list,
      };

      return valueNode;
    }

    const valueNode: CSTValueNode = {
      type: 'value',
      begin: kvNode ? kvNode.begin : result.begin,
      end: kvNode ? kvNode.end : result.end,
      value: kvNode ? kvNode : result,
    };

    return valueNode;
  }

  function parseValueParentheses(): CSTParenthesesNode {
    const leftParen = eat(SyntaxKind.LeftParen);
    const node = parseValue();
    const rightParen = eat(SyntaxKind.RightParen);

    const parenthesesNode: CSTParenthesesNode = {
      type: 'parentheses',
      begin: leftParen.begin,
      end: leftParen.end,
      leftParen,
      rightParen,
      children: [node],
    };

    return parenthesesNode;
  }

  function parseEquationValue(): CSTValueNode | CSTParenthesesNode | CSTErrorNode {
    const token = check();

    if (!token) {
      ++i;

      const errorNode: CSTErrorNode = {
        type: 'error',
        begin: tokens[tokensLength - 1].end,
        end: tokens[tokensLength - 1].end,
        message: 'Expected value: an integer, string, key:value pair of a list of values',
        children: [],
      };

      return errorNode;
    }

    if (token.syntaxKind === SyntaxKind.LeftParen) {
      return parseValueParentheses();
    }

    return parseValue();
  }

  function parseOperator(): Token | CSTErrorNode {
    const operator = eatOneOf(
      EquationOperators,
      'Expected "=", "!=", ">", ">=", "<", "<=", "is", "is not", "contains", "not contains" or "search',
    );

    if (isToken(operator)) {
      return operator;
    }

    // if operator is not a token, that means this is an error node
    // we need now to determine which of the scenarios is happening
    // and execute accordingly

    // check if the error actually has a beginning of a complex token
    // ("is" because "is empty" etc, "not" because "not contains" etc.)
    const maybeToken = operator.children[0];

    if (maybeToken && isToken(maybeToken)) {
      // this may be a beginning of a complex token, in that case we need to check
      // if next tokens are continuations of that token, and if they are,
      // we treat them as part of the operator error and not as the next language item
      const token = check();

      if (
        maybeToken.syntaxKind === SyntaxKind.Is &&
        ('not'.startsWith(token.value) || 'empty'.startsWith(token.value)) &&
        token.syntaxKind !== SyntaxKind.EOF
      ) {
        // situations like "is n" or "is emp"
        operator.children.push(token);
        operator.end = token.end;
        ++i;
      } else if (
        maybeToken.syntaxKind === SyntaxKind.Not &&
        'contains'.startsWith(token.value) &&
        token.syntaxKind !== SyntaxKind.EOF
      ) {
        // situation like "not cont"
        operator.children.push(token);
        operator.end = token.end;
        ++i;
      } else if (
        maybeToken.syntaxKind === SyntaxKind.IsNot &&
        'empty'.startsWith(token.value) &&
        token.syntaxKind !== SyntaxKind.EOF
      ) {
        // situation like "is not emp"
        operator.children.push(token);
        operator.end = token.end;
        ++i;
      }
    }

    // it's either some garbage or not even the first token is a complete
    // beginning of the complex operator token, so just return this error
    return operator;
  }

  function parseEquation(): CSTEquationNode {
    const left = eat(SyntaxKind.Identifier, 'Expected identifier');
    const operator = parseOperator();

    let right: ReturnType<typeof parseEquationValue> | undefined;

    if (
      isToken(operator) &&
      (operator.syntaxKind === SyntaxKind.IsEmpty || operator.syntaxKind === SyntaxKind.IsNotEmpty)
    ) {
      right = undefined;
    } else {
      right = parseEquationValue();
    }

    const isListInKeyValue = (node: CSTNode) => {
      if (node.type === 'keyValue') {
        const keyValueVal = (node as CSTKeyValueNode).value;

        if (isNode(keyValueVal) && keyValueVal.type === 'list') {
          return true;
        }
      }

      return false;
    };

    // is operator one of >, >=, <, <= ?
    const isSingleValueOperator = isToken(operator) && SingleValueOperators.includes(operator.syntaxKind);

    // is right side of equation a list?
    const isRightSideAList =
      right !== undefined &&
      right.type === 'value' &&
      isNode(right.value) &&
      (right.value.type === 'list' || isListInKeyValue(right.value));

    // don't allow >, >=, <, <=, search, contains, not contains to compare to list, it doesn't make sense
    // so let's just make it a parse error if this is the case
    if (isSingleValueOperator && isRightSideAList) {
      const valueErrorNode: CSTErrorNode = {
        type: 'error',
        begin: right!.begin,
        end: right!.end,
        message:
          'Invalid value for selected operator (>, >=, <, <=, search, contains, not contains do not allow list values)',
        children: [right!],
      };

      right = valueErrorNode;
    }

    const equationNode: CSTEquationNode = {
      type: 'equation',
      begin: left.begin,
      end: right ? right.end : operator.end,
      left,
      right,
      operator,
    };

    extendWhiteSpace(equationNode);

    return equationNode;
  }

  function parseBinaryOperation(leftNode: CSTNode): CSTBinaryOperationNode {
    const operator = eatOneOf(BinaryOperators);
    const rightNode = parseCondition();

    const binaryOperationNode: CSTBinaryOperationNode = {
      type: 'binaryOperation',
      begin: leftNode.begin,
      end: rightNode.end,
      left: leftNode,
      operator,
      right: rightNode,
    };

    extendWhiteSpace(binaryOperationNode);

    return binaryOperationNode;
  }

  function parseCondition(): CSTNode {
    let node: CSTNode = {
      type: 'error',
      begin: tokens[tokensLength - 1].end,
      end: tokens[tokensLength - 1].end,
      children: [],
      message: 'Unexpected EOF',
    } as CSTErrorNode;

    const token = check();

    if (!token) {
      return node;
    }

    if (token.syntaxKind === SyntaxKind.EOF) {
      ++i;

      return node;
    }

    if (token.syntaxKind === SyntaxKind.LeftParen) {
      node = parseParentheses();
    } else if (token.syntaxKind === SyntaxKind.Identifier) {
      node = parseEquation();
    } else {
      node = {
        type: 'error',
        begin: token.begin,
        end: token.end,
        children: [token],
        message: `Unexpected token in condition: '${token.value}', expected '(' or an identifier`,
      } as CSTErrorNode;

      ++i;
    }

    const nextToken = check();

    if (nextToken && BinaryOperators.includes(nextToken.syntaxKind)) {
      node = parseBinaryOperation(node);
    }

    extendWhiteSpace(node);

    return node;
  }

  if (tokensLength === 1) {
    // just EOF
    return root;
  }

  while (i < tokensLength) {
    root.children.push(parseCondition());

    // Commenting this for now, as this piece of code
    // may be OK to revert back if we want other error messages
    //
    //
    // if (i < tokensLength && tokens[i].syntaxKind !== SyntaxKind.EOF) {
    //   // we should stop parsing here, if there are any tokens left
    //   // that means this is an error

    //   const errorNode: CSTErrorNode = {
    //     type: 'error',
    //     begin: tokens[i].begin,
    //     end: tokens[i].end,
    //     message: `Unexpected token: ${tokens[i].value}`,
    //     children: [tokens[i]],
    //   };

    //   extendWhiteSpace(errorNode);

    //   root.children.push(errorNode);
    //   ++i;
    // }

    if (i < tokensLength && tokens[i].syntaxKind === SyntaxKind.EOF) {
      ++i;
    }
  }

  return root;
}
